/**
 * Behavior for snippets/swist-card.liquid: the "other colors" panel, in-place color preview
 * and the optional three-photo gallery (arrows and progress bar).
 * Listeners are delegated from the document once, so cards added later (infinite scroll,
 * filtering, section re-renders) work without per-card setup.
 */
(() => {
  if (window.swistCard) return;

  let pointer = '';
  /** @type {WeakMap<Element, object>} Original card content, restored after a color preview */
  const originals = new WeakMap();

  const cardOf = (/** @type {EventTarget | null} */ target) =>
    target instanceof Element ? target.closest('.swc-card') : null;

  /**
   * Opens or closes a card's color panel. Closing also restores the original color.
   * @param {Element | null} card
   * @param {boolean} open
   * @param {boolean} [focusPanel]
   */
  function setOpen(card, open, focusPanel = false) {
    if (!card) return;
    if (open) {
      for (const other of document.querySelectorAll('.swc-card.is-open, .swc-card.is-swatching')) {
        if (other !== card) setOpen(other, false);
      }
    } else {
      restore(card);
    }
    card.classList.toggle('is-open', open);
    card.querySelector('[data-swc-colors-toggle]')?.setAttribute('aria-expanded', String(open));
    if (open && focusPanel) {
      const active = card.querySelector('.swc__swatch.is-active') ?? card.querySelector('.swc__swatch');
      if (active instanceof HTMLElement) active.focus();
    }
  }

  /** Shows a sibling color's image, title and price in place of the card's own. */
  function preview(swatch) {
    const card = swatch.closest('.swc-card');
    if (!card) return;
    if (!originals.has(card)) originals.set(card, read(card));

    const slides = card.querySelector('[data-swc-slides]');
    if (slides) slides.scrollLeft = 0;

    const data = swatch.dataset;
    apply(card, {
      src: data.src,
      srcset: data.srcset,
      title: data.title,
      href: swatch.getAttribute('href'),
      price: data.price,
      was: data.was,
      pct: Number(data.pct) || 0,
      rating: Number(data.rating) || 0,
      ratingCount: Number(data.ratingCount) || 0,
    });

    for (const item of card.querySelectorAll('.swc__swatch')) item.classList.toggle('is-active', item === swatch);
    card.classList.add('is-swatching');
    card.classList.toggle('is-other', !swatch.classList.contains('is-current'));
  }

  function restore(card) {
    const original = originals.get(card);
    if (original) apply(card, original);
    for (const item of card.querySelectorAll('.swc__swatch')) {
      item.classList.toggle('is-active', item.classList.contains('is-current'));
    }
    card.classList.remove('is-swatching', 'is-other');
  }

  function read(card) {
    const img = card.querySelector('.swc__img--primary');
    const badge = card.querySelector('.swc__badge--sale');
    const rating = card.querySelector('[data-card-rating]');
    return {
      src: img?.getAttribute('src') ?? '',
      srcset: img?.getAttribute('srcset') ?? '',
      title: card.querySelector('.swc__title a')?.textContent ?? '',
      href: card.querySelector('.swc__link')?.getAttribute('href') ?? '',
      price: card.querySelector('.swc__now')?.textContent ?? '',
      was: card.querySelector('.swc__was')?.hidden ? '' : card.querySelector('[data-swc-was]')?.textContent ?? '',
      pct: badge ? Number(badge.textContent.replace(/\D/g, '')) || 0 : 0,
      rating: rating instanceof HTMLElement ? Number(rating.dataset.rating) || 0 : 0,
      ratingCount: rating instanceof HTMLElement ? Number(rating.dataset.ratingCount) || 0 : 0,
    };
  }

  function apply(card, data) {
    const img = card.querySelector('.swc__img--primary');
    if (img instanceof HTMLImageElement && data.src) {
      img.srcset = data.srcset ?? '';
      img.src = data.src;
    }

    const link = card.querySelector('.swc__link');
    const titleLink = card.querySelector('.swc__title a');
    if (data.href) {
      for (const slideLink of card.querySelectorAll('.swc__link')) slideLink.setAttribute('href', data.href);
      titleLink?.setAttribute('href', data.href);
    }
    if (data.title) {
      link?.setAttribute('aria-label', data.title);
      if (titleLink) titleLink.textContent = data.title;
    }

    const now = card.querySelector('.swc__now');
    if (now && data.price) now.textContent = data.price;

    const was = card.querySelector('.swc__was');
    const wasValue = card.querySelector('[data-swc-was]');
    if (was instanceof HTMLElement && wasValue) {
      was.hidden = !data.was;
      wasValue.textContent = data.was ?? '';
    }

    const badge = card.querySelector('.swc__badge--sale');
    if (badge instanceof HTMLElement) {
      badge.hidden = !(data.pct > 0);
      if (data.pct > 0) badge.textContent = `%${data.pct}`;
    }

    applyRating(card, data.rating, data.ratingCount);
  }

  /** Fills the card's review stars (snippets/card-rating.liquid) with another color's score. */
  function applyRating(card, rating = 0, count = 0) {
    const element = card.querySelector('[data-card-rating]');
    if (!(element instanceof HTMLElement)) return;

    const hasRating = rating > 0 && count > 0;
    element.hidden = !hasRating;
    if (!hasRating) return;

    const score = rating.toFixed(1);

    element.querySelector('.card-rating__stars')?.setAttribute('style', `--card-rating-fill: ${(rating / 5) * 100}%;`);
    const scoreElement = element.querySelector('[data-card-rating-score]');
    const countElement = element.querySelector('[data-card-rating-count]');
    const labelElement = element.querySelector('[data-card-rating-label]');
    if (scoreElement) scoreElement.textContent = score;
    if (countElement) countElement.textContent = `(${count})`;
    if (labelElement) {
      labelElement.textContent = (element.dataset.label ?? '').replace('[rating]', score).replace('[count]', String(count));
    }
  }

  /**
   * Gallery photos 2–3 are rendered with data-swc-src/-srcset (snippets/swist-card.liquid) so
   * nobody downloads them without touching the card; this swaps them in on first interest.
   * @param {Element | null} card
   */
  function loadSlides(card) {
    if (!card || card.hasAttribute('data-swc-slides-loaded')) return;
    card.setAttribute('data-swc-slides-loaded', '');
    for (const img of card.querySelectorAll('img[data-swc-src]')) {
      if (!(img instanceof HTMLImageElement)) continue;
      const srcset = img.getAttribute('data-swc-srcset');
      if (srcset) img.srcset = srcset;
      img.src = img.getAttribute('data-swc-src') ?? '';
      img.removeAttribute('data-swc-src');
      img.removeAttribute('data-swc-srcset');
    }
  }

  /** @param {Event} event */
  const loadSlidesFor = (event) => {
    const card = cardOf(event.target);
    if (card?.querySelector('img[data-swc-src]')) loadSlides(card);
  };

  // A finger landing on the card starts the download before the swipe; mouse users get it on hover
  document.addEventListener('touchstart', loadSlidesFor, { capture: true, passive: true });
  document.addEventListener('pointerover', (event) => {
    if (event.pointerType === 'mouse') loadSlidesFor(event);
  });
  document.addEventListener('focusin', loadSlidesFor);

  /** Syncs the progress bar and arrow states with the gallery's scroll position. */
  function syncGallery(slides) {
    const card = slides.closest('.swc-card');
    if (!card) return;
    const width = slides.clientWidth || 1;
    const max = slides.scrollWidth - slides.clientWidth;
    const pos = Math.abs(slides.scrollLeft);
    const index = Math.round(pos / width);

    const fill = card.querySelector('.swc__progress-fill');
    if (fill instanceof HTMLElement) fill.style.transform = `translateX(${index * 100}%)`;

    const prev = card.querySelector('.swc__arrow--prev');
    const next = card.querySelector('.swc__arrow--next');
    if (prev instanceof HTMLButtonElement) prev.disabled = pos <= 2;
    if (next instanceof HTMLButtonElement) next.disabled = pos >= max - 2;
  }

  document.addEventListener('pointerdown', (event) => (pointer = event.pointerType), { passive: true });

  // Scroll events don't bubble; capture them to follow every gallery on the page
  document.addEventListener(
    'scroll',
    (event) => {
      const slides = event.target;
      if (!(slides instanceof Element) || !slides.matches('[data-swc-slides]')) return;
      loadSlides(slides.closest('.swc-card'));
      syncGallery(slides);
    },
    { capture: true, passive: true }
  );

  /* Panel toggle and close, touch color preview, and closing panels on outside clicks */
  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const card = cardOf(target);

    if (!card) {
      for (const open of document.querySelectorAll('.swc-card.is-open, .swc-card.is-swatching')) setOpen(open, false);
      return;
    }

    const arrow = target?.closest('[data-swc-slide]');
    if (arrow) {
      event.preventDefault();
      const slides = card.querySelector('[data-swc-slides]');
      if (!slides) return;
      loadSlides(card);
      const isRtl = getComputedStyle(slides).direction === 'rtl';
      slides.scrollBy({
        left: Number(arrow.getAttribute('data-swc-slide')) * (isRtl ? -1 : 1) * slides.clientWidth,
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      });
      return;
    }

    const toggle = target?.closest('[data-swc-colors-toggle]');
    if (toggle) {
      // detail is 0 for keyboard activation: move focus into the panel
      setOpen(card, toggle.getAttribute('aria-expanded') !== 'true', event.detail === 0);
      return;
    }

    if (target?.closest('[data-swc-colors-close]')) {
      setOpen(card, false);
      card.querySelector('[data-swc-colors-toggle]')?.focus({ preventScroll: true });
      return;
    }

    const swatch = target?.closest('[data-swc-swatch]');
    if (swatch) {
      // On touch the first tap previews the color in the card, the second opens it
      const isTouch = pointer === 'touch' || pointer === 'pen';
      if (isTouch && !swatch.classList.contains('is-active')) {
        event.preventDefault();
        preview(swatch);
      }
    }
  });

  document.addEventListener('pointerover', (event) => {
    if (event.pointerType !== 'mouse' || !(event.target instanceof Element)) return;
    const swatch = event.target.closest('.swc-card [data-swc-swatch]');
    if (swatch) preview(swatch);
  });

  document.addEventListener('pointerout', (event) => {
    if (event.pointerType !== 'mouse' || !(event.target instanceof Element)) return;
    const card = cardOf(event.target);
    if (!card) return;
    const to = event.relatedTarget instanceof Element ? event.relatedTarget : null;

    // The panel opens from the color pill, so leaving the panel ends the preview
    const panel = event.target.closest('.swc__colors');
    const backToPanel = to?.closest('.swc__colors, [data-swc-colors-toggle]');
    if (panel && !backToPanel && !card.classList.contains('is-open')) {
      restore(card);
      return;
    }

    if (to && card.contains(to)) return;
    setOpen(card, false);
  });

  document.addEventListener('focusin', (event) => {
    // Android focuses a link on tap before the click: previewing here made that first tap navigate
    if (pointer === 'touch' || pointer === 'pen') return;
    const swatch = event.target instanceof Element ? event.target.closest('.swc-card [data-swc-swatch]') : null;
    if (swatch) preview(swatch);
  });

  // Keyboard focus after a tap still previews
  document.addEventListener('keydown', () => (pointer = ''), { passive: true });

  document.addEventListener('focusout', (event) => {
    const card = cardOf(event.target);
    if (!card || (event.relatedTarget instanceof Node && card.contains(event.relatedTarget))) return;
    if (card.matches(':hover')) return;
    setOpen(card, false);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || !(event.target instanceof Element)) return;
    const card = event.target.closest('.swc-card.is-open, .swc-card.is-swatching');
    if (!card) return;
    const returnFocus = event.target.closest('.swc__colors') !== null;
    setOpen(card, false);
    if (returnFocus) card.querySelector('[data-swc-colors-toggle]')?.focus();
  });

  /*
   * In an infinite-scroll grid, record the clicked card's page and anchor in the URL before
   * leaving, so "back" reloads that page and main-collection scrolls to the card
   * (the same contract product-card.js follows for theme cards).
   */
  document.addEventListener('click', (event) => {
    if (event.defaultPrevented || window.Shopify?.designMode) return;
    const link = event.target instanceof Element ? event.target.closest('.swc-card a[href]') : null;
    const item = link?.closest('results-list[infinite-scroll="true"] li[data-page]');
    if (!(item instanceof HTMLElement) || !item.id) return;

    const url = new URL(location.href);
    url.searchParams.set('page', item.dataset.page ?? '1');
    url.hash = item.id;
    history.replaceState(history.state, '', url);
  });

  /*
   * Promo ticker: one shared timer steps the messages of tickers that are on screen.
   * Tickers are picked up on each tick, so cards added later (infinite scroll, filtering,
   * section re-renders) join in without a MutationObserver.
   */
  const PROMO_SELECTOR = '.swc__promo:is([data-n="2"], [data-n="3"]):not(.swc__promo--off)';
  const PROMO_INTERVAL = 3000;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  /** @type {Set<HTMLElement>} */
  const visiblePromos = new Set();
  /** @type {WeakSet<Element>} */
  const watchedPromos = new WeakSet();
  let promoTick = 0;

  const promoObserver = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (!(entry.target instanceof HTMLElement)) continue;
      if (entry.isIntersecting) visiblePromos.add(entry.target);
      else visiblePromos.delete(entry.target);
    }
  });

  function watchPromos() {
    for (const promo of document.querySelectorAll(PROMO_SELECTOR)) {
      if (watchedPromos.has(promo)) continue;
      watchedPromos.add(promo);
      promoObserver.observe(promo);
    }
  }

  function stepPromos() {
    watchPromos();
    if (document.hidden || reducedMotion.matches) return;
    promoTick += 1;
    for (const promo of visiblePromos) {
      if (!promo.isConnected) {
        visiblePromos.delete(promo);
        promoObserver.unobserve(promo);
        continue;
      }
      const count = Number(promo.dataset.n) || 1;
      promo.dataset.prev = String((promoTick - 1) % count);
      promo.dataset.step = String(promoTick % count);
    }
  }

  watchPromos();
  setInterval(stepPromos, PROMO_INTERVAL);

  window.swistCard = { setOpen, preview, restore };
})();
